import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Sparkles, ArrowRight, History, BarChart3, Clock, ListChecks } from "lucide-react";
import api from "@/lib/api";
import { Header } from "@/pages/CollegePredictorPage";
import { TYPE_ICON, TYPE_COLOR, PatternBadge, Stat, SampleNote, Crumb, err, startTest, trackEvent, fmtTime } from "./shared";

export default function PrepExamHub() {
  const { examId } = useParams();
  const navigate = useNavigate();
  const [detail, setDetail] = useState(null);
  const [perf, setPerf] = useState(null);

  useEffect(() => {
    api.get(`/test-prep/exams/${examId}`).then((r) => setDetail(r.data)).catch((e) => toast.error(err(e)));
    api.get(`/test-prep/performance?exam_id=${examId}`).then((r) => setPerf(r.data)).catch(() => {});
  }, [examId]);

  if (!detail) return <div className="p-8 text-slate-400">Loading...</div>;
  const { exam, subjects, test_types } = detail;
  const recs = perf?.recommendations || [];

  const onRec = (r) => { trackEvent("recommendation_clicked", { test_id: r.test_id, exam_id: examId }); startTest(navigate, r.test_id, true); };

  return (
    <div data-testid="prep-exam-hub" className="mx-auto max-w-6xl px-4 sm:px-8 py-8">
      <Crumb items={[{ to: "/prep", label: "Test Preparation" }, { label: exam.name }]} />
      <Header icon={ListChecks} eyebrow={`${exam.level === "class12" ? "Class 12" : `${exam.level.toUpperCase()} Entrance`} · Session ${exam.session}`}
        title={`${exam.name} ${exam.level !== "class12" ? exam.session : ""}`} desc={`${subjects.length} subjects · ${exam.question_count} practice questions`} />
      <div className="-mt-4 mb-6 flex flex-wrap items-center gap-2">
        <PatternBadge status={exam.pattern_status} />
        {exam.pattern && <span className="text-xs text-slate-500">Mock: {exam.pattern.questions} Q · {exam.pattern.total_marks} marks · {exam.pattern.minutes} min · +{exam.pattern.marks_correct} / {exam.pattern.marks_wrong}{exam.pattern_note ? ` · ${exam.pattern_note}` : ""}</span>}
      </div>
      <SampleNote />

      <div className="mt-6 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Stat label="Tests taken" value={perf?.tests_taken ?? 0} testid="hub-tests-taken" />
        <Stat label="Questions attempted" value={perf?.questions_attempted ?? 0} tone="indigo" testid="hub-questions" />
        <Stat label="Average score" value={perf?.avg_score ?? 0} tone="emerald" testid="hub-avg-score" />
        <Stat label="Accuracy" value={`${perf?.avg_accuracy ?? 0}%`} tone="amber" testid="hub-accuracy" />
      </div>

      <section className="mt-8 rounded-2xl border border-cyan-400/25 bg-gradient-to-r from-[#0F2A33] to-[#131C2E] p-5">
        <div className="flex items-center gap-2"><Sparkles className="h-4 w-4 text-cyan-400" /><h2 className="text-base font-bold md:text-lg">Recommended next test</h2></div>
        <p className="mt-1 text-xs text-slate-400">Based on your actual test performance (rule-based: topic &lt;50%, chapter &lt;60%, subject &lt;65% accuracy).</p>
        <div className="mt-3 grid gap-3 md:grid-cols-3">
          {recs.map((r) => (
            <button key={r.test_id} data-testid={`rec-${r.test_type}`} onClick={() => onRec(r)}
              className="rounded-xl border border-white/10 bg-white/[0.03] p-4 text-left transition-all hover:border-cyan-400/40">
              <p className="text-sm font-semibold">{r.title}</p>
              <p className="mt-1 text-xs text-slate-400">{r.reason}</p>
              <p className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-cyan-300">Start now <ArrowRight className="h-3.5 w-3.5" /></p>
            </button>
          ))}
        </div>
      </section>

      <section className="mt-8">
        <h2 className="text-base font-bold md:text-lg">Choose a test level</h2>
        <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
          {test_types.map((t) => {
            const Icon = TYPE_ICON[t.id];
            const disabled = t.id === "mock" && !exam.pattern;
            return (
              <Link key={t.id} to={disabled ? "#" : `/prep/exam/${examId}/tests?type=${t.id}`} data-testid={`type-card-${t.id}`}
                onClick={(e) => disabled && e.preventDefault()}
                className={`rounded-2xl border p-4 transition-all ${disabled ? "cursor-not-allowed border-white/5 bg-white/[0.02] opacity-60" : "border-white/8 bg-[#131C2E] hover:border-cyan-400/40"}`}>
                <span className={`inline-flex rounded-lg border p-2 ${TYPE_COLOR[t.id]}`}><Icon className="h-4 w-4" /></span>
                <p className="mt-3 font-semibold">{t.label}</p>
                <p className="mt-0.5 text-xs text-slate-400">{disabled ? "Needs a verified exam pattern — not yet configured." : t.desc}</p>
              </Link>
            );
          })}
        </div>
      </section>

      <section className="mt-8 grid gap-6 lg:grid-cols-3">
        <div className="rounded-2xl border border-white/8 bg-[#131C2E] p-5 lg:col-span-2">
          <div className="flex items-center justify-between"><h3 className="font-semibold">Recent tests</h3>
            <Link to="/prep/history" className="inline-flex items-center gap-1 text-xs text-cyan-300"><History className="h-3.5 w-3.5" /> All history</Link></div>
          {(perf?.recent || []).length === 0 && <p className="mt-3 text-sm text-slate-500">No tests yet. Start with a Quick Test.</p>}
          <div className="mt-3 space-y-2">
            {(perf?.recent || []).map((a) => (
              <Link key={a.id} to={`/prep/result/${a.id}`} data-testid="recent-attempt-row" className="flex items-center justify-between rounded-xl border border-white/5 px-4 py-3 text-sm hover:bg-white/[0.03]">
                <div><p className="font-medium">{a.title}</p><p className="text-xs text-slate-500">{new Date(a.submitted_at).toLocaleString()}</p></div>
                <div className="text-right"><p className="font-mono-data font-bold text-cyan-300">{a.result.score}/{a.result.total_marks}</p><p className="text-xs text-slate-500">{a.result.accuracy}% · <Clock className="inline h-3 w-3" /> {fmtTime(a.result.time_taken_sec)}</p></div>
              </Link>
            ))}
          </div>
        </div>
        <div className="rounded-2xl border border-white/8 bg-[#131C2E] p-5">
          <div className="flex items-center justify-between"><h3 className="font-semibold">Strong & weak topics</h3>
            <Link to={`/prep/performance?exam=${examId}`} className="inline-flex items-center gap-1 text-xs text-cyan-300"><BarChart3 className="h-3.5 w-3.5" /> Details</Link></div>
          <TopicList label="Strong" rows={perf?.strong_topics} tone="emerald" />
          <TopicList label="Weak" rows={perf?.weak_topics} tone="rose" />
          {!(perf?.strong_topics?.length || perf?.weak_topics?.length) && <p className="mt-3 text-xs text-slate-500">Attempt at least 3 questions in a topic to see it here.</p>}
        </div>
      </section>
    </div>
  );
}

const TopicList = ({ label, rows = [], tone }) => rows.length ? (
  <div className="mt-3">
    <p className={`text-[11px] uppercase tracking-wide text-${tone}-300`}>{label}</p>
    <ul className="mt-1 space-y-1 text-sm">{rows.map((t) => <li key={t.id} className="flex justify-between"><span>{t.name}</span><span className="font-mono-data text-slate-400">{t.accuracy}%</span></li>)}</ul>
  </div>
) : null;
